<template>
  <div v-if="isOpen" class="description-selector-overlay" @click="closeModal">
    <div class="description-selector" @click.stop>
      <div class="description-selector__header">
        <h3>Выберите описание</h3>
        <button class="description-selector__closeBtn" @click="closeModal">
          <img src="../../assets/images/close.svg" alt="close" />
        </button>
      </div>

      <div class="description-selector__filters">
        <div class="description-selector__search">
          <div class="description-selector__searchInput">
            <img src="../../assets/images/search.svg" alt="search" />
            <input
              type="text"
              v-model="searchName"
              placeholder="Поиск по названию..."
              @input="onSearchInput"
            />
          </div>
        </div>
      </div>

      <div class="description-selector__content">
        <div class="description-selector__loader" v-if="loading">
          <Loader />
        </div>

        <div class="description-selector__list" v-if="!loading">
          <div
            v-for="description in descriptions"
            :key="description.id"
            class="description-selector__item"
            :class="{ selected: selectedDescription && selectedDescription.id === description.id }"
            @click="selectDescription(description)"
          >
            <div class="description-selector__itemInfo">
              <div class="description-selector__itemName">{{ description.name }}</div>
              <div class="description-selector__itemId">ID: {{ description.id }}</div>
              <div class="description-selector__itemValues">
                {{ getValuesPreview(description.values) }}
              </div>
            </div>
            <div class="description-selector__itemActions">
              <button
                class="description-selector__selectBtn"
                @click.stop="selectDescription(description)"
              >
                {{
                  selectedDescription && selectedDescription.id === description.id
                    ? 'Выбрано'
                    : 'Выбрать'
                }}
              </button>
            </div>
          </div>
          <div v-if="descriptions.length === 0" class="description-selector__empty">
            Описания не найдены
          </div>
        </div>

        <div class="description-selector__pagination" v-if="!loading && pagination.total > 0">
          <div class="description-selector__paginationInfo">
            Показано {{ pagination.skip + 1 }}-{{
              Math.min(pagination.skip + pagination.limit, pagination.total)
            }}
            из {{ pagination.total }}
          </div>
          <div class="description-selector__paginationControls">
            <button
              class="description-selector__paginationBtn"
              :disabled="pagination.skip === 0"
              @click="prevPage"
            >
              <img src="../../assets/images/arrowLeft.svg" alt="prev" />
              Назад
            </button>

            <div class="description-selector__paginationPages">
              <button
                v-for="page in getVisiblePages()"
                :key="page"
                class="description-selector__paginationPage"
                :class="{ active: page === getCurrentPage() }"
                @click="goToPage(page - 1)"
              >
                {{ page }}
              </button>
            </div>

            <button
              class="description-selector__paginationBtn"
              :disabled="pagination.skip + pagination.limit >= pagination.total"
              @click="nextPage"
            >
              Вперед
              <img src="../../assets/images/right-arrow.svg" alt="next" />
            </button>
          </div>
        </div>
      </div>

      <div class="description-selector__footer">
        <button class="description-selector__cancelBtn" @click="closeModal">Отмена</button>
        <button
          class="description-selector__confirmBtn"
          @click="confirmSelection"
          :disabled="!selectedDescription"
        >
          Выбрать
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '../Loader/Loader.vue'
import DescriptionsApi from '@/utils/descriptionsApi'

export default {
  name: 'DescriptionSelector',
  components: {
    Loader
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    selectedGame: {
      type: Number,
      required: true
    },
    currentDescriptionId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      descriptions: [],
      loading: false,
      searchName: '',
      searchTimeout: null,
      selectedDescription: null,
      pagination: {
        skip: 0,
        limit: 10,
        total: 0,
        pages: 0
      },
      api: null
    }
  },
  computed: {
    baseApiUrl() {
      return 'https://mg.apineural.com/api'
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.loadDescriptions()
        this.selectedDescription = this.currentDescriptionId
          ? { id: this.currentDescriptionId }
          : null
      }
    },
    selectedGame() {
      if (this.isOpen) {
        this.pagination.skip = 0
        this.searchName = ''
        this.loadDescriptions()
      }
    }
  },
  methods: {
    updateApiInstance() {
      this.api = new DescriptionsApi(this.baseApiUrl, this.$store.state.tokens.access)
    },
    async loadDescriptions() {
      this.loading = true
      try {
        this.updateApiInstance()
        const data = await this.api.getDescriptionsList({
          page: this.getCurrentPage(),
          limit: this.pagination.limit,
          name: this.searchName,
          game: this.selectedGame
        })

        this.descriptions = data.items || []
        this.pagination.total = data.total || 0
        this.pagination.pages = data.pages || 0
      } catch (error) {
        console.error('Error loading descriptions:', error)
        this.descriptions = []
        this.pagination.total = 0
        this.pagination.pages = 0
      } finally {
        this.loading = false
      }
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.pagination.skip = 0
        this.loadDescriptions()
      }, 500)
    },
    selectDescription(description) {
      this.selectedDescription = description
      this.confirmSelection()
    },
    confirmSelection() {
      if (this.selectedDescription) {
        this.$emit('select', this.selectedDescription)
        this.closeModal()
      }
    },
    closeModal() {
      this.$emit('close')
    },
    nextPage() {
      if (this.pagination.skip + this.pagination.limit < this.pagination.total) {
        this.pagination.skip += this.pagination.limit
        this.loadDescriptions()
      }
    },
    prevPage() {
      if (this.pagination.skip > 0) {
        this.pagination.skip = Math.max(0, this.pagination.skip - this.pagination.limit)
        this.loadDescriptions()
      }
    },
    goToPage(page) {
      this.pagination.skip = page * this.pagination.limit
      this.loadDescriptions()
    },
    getValuesPreview(values) {
      const languages = Object.keys(values || {})
      if (languages.length === 0) return 'Нет значений'
      if (languages.length === 1)
        return `${languages[0]}: ${values[languages[0]].substring(0, 30)}...`
      return `${languages.length} языков (${languages.join(', ')})`
    },
    getVisiblePages() {
      const totalPages =
        this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.limit)
      const currentPage = this.getCurrentPage()
      const maxVisible = 5

      if (totalPages <= maxVisible) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
      }

      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    getCurrentPage() {
      return Math.floor(this.pagination.skip / this.pagination.limit) + 1
    }
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }
}
</script>

<style lang="scss" scoped>
.description-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.description-selector {
  background: #131732;
  border-radius: 12px;
  border: 1px solid #646464;
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    border-bottom: 1px solid #2a2f4e;

    h3 {
      color: white;
      font-size: 20px;
      font-weight: 600;
      margin: 0;
    }
  }

  &__closeBtn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.3s ease;

    img {
      width: 16px;
      height: 16px;
      filter: brightness(0) invert(1);
    }

    &:hover {
      background: #2a2f4e;
    }
  }

  &__filters {
    padding: 20px 30px;
    border-bottom: 1px solid #2a2f4e;
  }

  &__search {
    max-width: 400px;
  }

  &__searchInput {
    display: flex;
    align-items: center;
    background: #1a1f3e;
    border: 1px solid #646464;
    border-radius: 6px;
    padding: 12px 16px;

    img {
      width: 16px;
      height: 16px;
      margin-right: 12px;
      filter: brightness(0) invert(0.6);
    }

    input {
      background: none;
      border: none;
      color: white;
      font-size: 14px;
      font-family: Gilroy, sans-serif;
      width: 100%;
      outline: none;

      &::placeholder {
        color: #888;
      }
    }
  }

  &__content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: 0 30px;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #2a2f4e;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #1a1f3e;
      margin: 0 -30px;
      padding: 16px 30px;
    }

    &.selected {
      background: #2a2f4e;
      margin: 0 -30px;
      padding: 16px 30px;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__itemInfo {
    flex: 1;
  }

  &__itemName {
    color: white;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__itemId {
    color: #7457ff;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__itemValues {
    color: #ccc;
    font-size: 13px;
    line-height: 1.4;
  }

  &__itemActions {
    margin-left: 20px;
  }

  &__selectBtn {
    background: #7457ff;
    border: none;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background: #6a4fd8;
    }

    &:disabled {
      background: #2a2f4e;
      color: #888;
      cursor: not-allowed;
    }
  }

  &__empty {
    text-align: center;
    color: #888;
    padding: 40px;
    font-size: 16px;
  }

  &__pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    border-top: 1px solid #2a2f4e;
    background: #1a1f3e;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  &__paginationInfo {
    color: #ccc;
    font-size: 14px;
    font-weight: 500;
  }

  &__paginationControls {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__paginationBtn {
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    outline: none;
    background: #131732;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #646464;

    img {
      width: 14px;
      height: 14px;
      filter: brightness(0) invert(1);
    }

    &:hover:not(:disabled) {
      background: #2a2f4e;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__paginationPages {
    display: flex;
    gap: 5px;
  }

  &__paginationPage {
    border: none;
    outline: none;
    background: #131732;
    color: white;
    width: 35px;
    height: 35px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #646464;

    &:hover {
      background: #2a2f4e;
    }

    &.active {
      background: #7457ff;
      border-color: #7457ff;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    padding: 20px 30px;
    border-top: 1px solid #2a2f4e;
    background: #1a1f3e;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__cancelBtn {
    border: none;
    outline: none;
    border-radius: 6px;
    padding: 12px 24px;
    background: #2a2f4e;
    color: white;
    font-size: 15px;
    font-weight: 600;
    font-family: Gilroy, sans-serif;
    transition: all 0.3s ease;
    cursor: pointer;
    border: 1px solid #646464;

    &:hover {
      background: #3a3f5e;
    }
  }

  &__confirmBtn {
    border: none;
    outline: none;
    border-radius: 6px;
    padding: 12px 24px;
    background: #7457ff;
    color: white;
    font-size: 15px;
    font-weight: 600;
    font-family: Gilroy, sans-serif;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #6a4fd8;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
